import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAppLoading } from "./useAppLoading";

export interface IOnboarding {
  _id?: string;
  title?: string;
  page?: string;
  image_en?: string;
  image_nl?: string;
  showHeader?: boolean;
  showFooterProgress?: boolean;
  showNextButton?: boolean;
  resizeType?: "contain" | "cover";
  enTopBackgroundColor?: string;
  nlTopBackgroundColor?: string;
  enBottomBackgroundColor?: string;
  nlBottomBackgroundColor?: string;
  showCustomButton?: boolean;
  enCustomButtonText?: string;
  nlCustomButtonText?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

interface IStore {
  /** State */
  oneOnboarding: IOnboarding | null;
  allOnboardings: IOnboarding[];
  addedOnboarding?: string | null;
  updatedOnboarding?: string | null;
  /** Callbacks */
  getAllOnboardings: () => void;
  getOneOnboarding: (id: string) => void;
  addOnboarding: (body: IOnboarding) => void;
  deleteOnboarding: (id: string) => void;
  updateOnboarding: (id: string, body: IOnboarding) => void;
  clearAddedOnboarding: () => void;
  clearUpdateOnboarding: () => void;
}

export const useOnboardings = create<IStore>()((set) => ({
  /** State */
  allOnboardings: [],
  oneOnboarding: null,
  addedOnboarding: null,
  updatedOnboarding: null,
  /** Callbacks */
  getAllOnboardings: async () => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get("onboardings");
      if (response?.data) {
        set({ allOnboardings: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching Onboardings", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getOneOnboarding: async (id) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`onboardings/${id}`);
      set({ oneOnboarding: response.data });
    } catch (error) {
      LOG.error("Error on fetching Onboarding", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addOnboarding: async (body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("onboardings", body);
      if (response?.data) {
        const newOnboarding = response.data;
        set((state) => ({
          allOnboardings: [newOnboarding, ...state.allOnboardings],
          addedOnboarding: newOnboarding._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on adding new Onboarding", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteOnboarding: async (id) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      await axios.delete(`onboardings/${id}`);
      set((state) => ({
        allOnboardings: state.allOnboardings.filter(
          (onboarding) => onboarding._id !== id
        ),
      }));
    } catch (error) {
      LOG.error("Error on deleting an Onboarding", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateOnboarding: async (id, body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`onboardings/${id}`, body);
      if (response?.data) {
        const updatedOnboarding = response.data;
        set((state) => ({
          allOnboardings: state.allOnboardings.map((onboarding) => {
            if (onboarding._id === updatedOnboarding._id) {
              return updatedOnboarding;
            }
            return onboarding;
          }),
          updatedOnboarding: updatedOnboarding._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on updating an Onboarding", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearAddedOnboarding: () => {
    set({ addedOnboarding: null });
  },
  clearUpdateOnboarding: () => {
    set({ updatedOnboarding: null });
  },
}));
